// src/services/aeo/clientDocsWriteMock.ts
//
// The Docs WRITE sample store, v2: a fake server for phase 2 while the
// backend does not exist. It serves only on a local fixtures run (the dev
// server, with VITE_ENABLE_OURA_DOCS_WRITE_LIVE, VITE_ENABLE_OURA_DOCS_LIVE
// and VITE_ENABLE_OURA_LIVE_API all unset: DOCS_WRITE_SAMPLE_SERVES). A
// build never serves it. DELETE once the write routes are live.
//
// It writes into Phase 1's sample store (clientDocsMock.ts), never a copy:
// a question sent, an answer, a share, a resolve or a rename changes the
// Drive-shaped files, threads and replies there, so Phase 1's rules
// recompute the board, the drawer, All clients and the merchant's list. What
// Drive does not hold (drafted questions, sent emails, filed reports,
// summaries, client files) lives in this store, persisted under its own key.
//
// The seed (rev 1), on nyonya-table, added to the Phase 1 fixtures:
//   - GBP Pack Mont Kiara (F2): 4 numbered questions on record; Siew Ling
//     answered 2 in Oura: question 1 Agreed (resolved, approval of record),
//     question 2 Change (needs us); a review email Angel sent.
//   - Copy Pack Home (F7): not shared, with 1 drafted question.
//   - GBP Pack Bukit Bintang (F3): every question approved, ready to file.
//   - Answer Plan (F1): "Please approve the new opening paragraph" resolved by
//     us with no client agreement: the amber flag.
//   - A subfolder shared with Siew Ling, so Create refuses there.
//   - Competitor Watch (F8, rev 3): Dana's open note on the title line, so
//     Oura will not rewrite that line until it is resolved (lane 3).
//   - Skills: none added.
//
// Dev tools, in the browser console (sample store only):
//   window.__ouraResetDocsWrite()   reseed this store AND Phase 1's (one sample)

import type { DocThread, DocsConnection, DocsErrorCode, DocsResult, DocumentDetail, MerchantDocument, StaffDocument } from "./clientDocsTypes";
import type {
    ApprovalRecord,
    ApprovalRecordRow,
    ApprovedReport,
    ClientAnswerIn,
    ClientEmailCheck,
    ClientEmailDraft,
    ClientEmailPreview,
    ClientEmailPurpose,
    ClientEmailSendIn,
    ClientEmailState,
    ClientFile,
    CreateDocIn,
    CreateDocResult,
    CreateTarget,
    DocLinesIn,
    DocMessageDraft,
    DocMessageIn,
    DocMessageKind,
    DocMessagePerson,
    DocQuestion,
    DocSummary,
    DocTemplate,
    DocsWriteStatus,
    FileApprovedResult,
    LogAnswerIn,
    MerchantAnswered,
    PlaceholderCleanUp,
    PlaceholderCleanUpIn,
    QuestionCheck,
    QuestionIn,
    QuestionPatchIn,
    RenameIn,
    RerunFillResult,
    ResolveAgreedIn,
    ResolveIn,
    SentDocMessage,
    ShareCheck,
    ShareCheckItem,
    ShareFixCode,
    SkillInput,
    SkillLibrary,
    SkillRun,
    SpendCap,
} from "./clientDocsWriteTypes";
import {
    docsDevMode,
    docsFailure,
    docsMockDelayMs,
    docsStoreApi,
    registerDocsDecorators,
    registerDocsSeedExtension,
    resetDocsStore,
    sharedNoticeLead,
    type MockAuthor,
    type MockBusiness,
    type MockFile,
    type MockReply,
    type MockThread,
} from "./clientDocsMock";
import {
    BASE_CODE_RE,
    CODE_RE,
    DOC_EXPECTED,
    OURA_ACCOUNT,
    codeChipsOf,
    customLabel,
    customLabelProblem,
    defaultReplyBy,
    docNameOf,
    isOurEmail,
    joinCodes,
    merchantDocName,
    myDate,
    myInstant,
    nextVersion,
    parseDocName,
    questionCommentOf,
    questionListLineOf,
    resolveCarriesAgreement,
    shiftWorkingDays,
    versionLineOf,
    type ClientWord,
} from "./clientDocsRules";
import { DOCS_WRITE_SAMPLE_SERVES } from "../../oura/docs/writeFlag";

/* ------------------------------------------------------------------------- *
 * The store
 * ------------------------------------------------------------------------- */

interface WriteStore {
    version: 2;
    seededOn: string;
    /** Questions drafted in Oura, not sent yet, by document id. */
    drafts: Record<string, DocQuestion[]>;
    /** Who drafted and sent each question, by question id (the thread holds the rest). */
    questionMeta: Record<string, { createdBy: string; createdAt: string; sentAt: string | null }>;
    messages: Record<string, SentDocMessage[]>;
    reports: Record<string, ApprovedReport[]>;
    summaries: Record<string, DocSummary>;
    cleanUps: Record<string, PlaceholderCleanUp>;
    clientFiles: Record<string, ClientFile>;
    seq: number;
}

const STORE_KEY = "__oura_docs_write_store_v2__";
/** The Drive-shaped fixtures this store adds to Phase 1's; bump to re-apply them to saved stores. */
const SEED_REV = 3;
const SAMPLE_ID = "nyonya-table";
const NYO_SHARED_FOLDER = "1NyonyaSharedWithSiewLing9xT4";
const SIEW: MockAuthor = { email: "siewling@nyonyatable.com", name: "Siew Ling" };
const GDOC = "application/vnd.google-apps.document";
const WORD_MIMES = ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/msword"];

/** Placeholders tonight (YW, 2 Oct 2026): no spend is measured, so none is shown as real. */
const SKILL_CAP: SpendCap = { monthCapUsd: 100, alertAtUsd: 80, spentUsd: null, stopped: false };
const SUMMARY_CAP: SpendCap = { monthCapUsd: 20, alertAtUsd: 16, spentUsd: null, stopped: false };

/** Assigned below the seed, once the fixtures it reads are defined. */
let store: WriteStore;

function blank(): WriteStore {
    return { version: 2, seededOn: myDate(new Date()), drafts: {}, questionMeta: {}, messages: {}, reports: {}, summaries: {}, cleanUps: {}, clientFiles: {}, seq: 100 };
}

function load(): WriteStore {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as WriteStore;
            if (parsed && parsed.version === 2 && parsed.drafts && parsed.messages) return parsed;
        }
    } catch {
        /* private mode, or a stale shape: reseed */
    }
    return seedWriteStore(blank());
}

function save(): void {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch {
        /* ignore */
    }
}

/** Reseed this store and Phase 1's, which carries this store's Drive fixtures. Exposed as `window.__ouraResetDocsWrite()`. */
export function resetDocsWriteStore(): void {
    store = seedWriteStore(blank());
    save();
    resetDocsStore();
}

const nextKey = (p: string) => `${p}_w${++store.seq}`;

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v)) as T;
}

/**
 * Every sample call goes through here: the Phase 1 dev mode first (error
 * fails, slow waits 3 s), then any folder read that came due, then the work.
 * A write that succeeded saves both stores and tells every open screen; a
 * read tells nobody, so a screen that reloads on the broadcast never loops.
 */
async function respond<T>(work: () => DocsResult<T>, write = true): Promise<DocsResult<T>> {
    const mode = docsDevMode();
    await new Promise((r) => window.setTimeout(r, docsMockDelayMs(220)));
    if (mode === "error") return docsFailure("network");
    if (docsStoreApi.settle()) docsStoreApi.save();
    const result = work();
    if (result.ok && write) {
        save();
        docsStoreApi.commit();
    }
    return result.ok ? { ok: true, data: clone(result.data) } : result;
}
const read = <T>(work: () => DocsResult<T>) => respond(work, false);

const ok = <T>(data: T): DocsResult<T> => ({ ok: true, data });
const notFound = (message = "That document is no longer in the folder.") => docsFailure("notFound", { status: 404, message });
const refuse = (code: DocsErrorCode, status: number, message?: string, extra: { existingId?: string | null; badCodes?: string[] | null } = {}) =>
    docsFailure("rejected", { code, status, message: message ?? null, ...extra });

/* ------------------------------------------------------------------------- *
 * Reading the documents store
 * ------------------------------------------------------------------------- */

function hit(documentId: string): { f: MockFile; b: MockBusiness } | null {
    return docsStoreApi.findDoc(documentId);
}

function threadOf(f: MockFile, threadId: string): MockThread | null {
    return f.threads?.find((t) => t.id === threadId) ?? null;
}

const staff = () => docsStoreApi.staffName;
const now = () => docsStoreApi.nowIso();
const today = () => docsStoreApi.today();

/** A reply from Oura, as oura@, signed with the staff member's name. */
function ourReply(text: string, action: MockReply["action"] = "reply"): MockReply {
    return { id: docsStoreApi.nextId("rep"), by: docsStoreApi.ouraAuthor(staff()), text, at: now(), action, where: "oura", signedBy: staff() };
}

/** A reply oura@ writes for the client (B26): it counts as theirs. */
function onBehalfReply(client: MockAuthor, text: string, extra: Partial<MockReply> = {}): MockReply {
    return { id: docsStoreApi.nextId("rep"), by: { email: OURA_ACCOUNT, name: "ourteam" }, text, at: now(), action: "reply", where: "oura", onBehalfOf: client, via: "oura", ...extra };
}

function touch(f: MockFile): void {
    f.modifiedAt = now();
}

/** The client's last words on a thread, from the computed thread (B26). */
function lastClientWord(t: DocThread): ClientWord | null {
    let word: ClientWord | null = t.openedBy.side === "client" ? { text: t.text } : null;
    for (const p of t.posts) {
        if (p.author.side === "client" && p.action === "reply" && p.text.trim()) word = { text: p.text, answer: p.answer ?? null };
    }
    return word;
}

/** The questions Oura keeps for a doc: drafts first, then the sent ones by number. */
function questionListOf(d: DocumentDetail, f: MockFile | null): DocQuestion[] {
    const sent: DocQuestion[] = [];
    for (const t of f?.threads ?? []) {
        const q = t.question;
        if (!q) continue;
        const computed = d.threads.find((x) => x.id === t.id);
        const meta = store.questionMeta[q.id];
        sent.push({
            id: q.id,
            code: q.code,
            kind: q.kind,
            text: q.text,
            options: q.options,
            number: q.number,
            commentId: computed?.driveCommentId ?? null,
            threadId: t.id,
            state: "sent",
            createdBy: meta?.createdBy ?? docsStoreApi.staffName,
            createdAt: meta?.createdAt ?? computed?.openedAt ?? now(),
            sentAt: meta?.sentAt ?? computed?.openedAt ?? null,
        });
    }
    sent.sort((x, y) => (x.number ?? 0) - (y.number ?? 0));
    return [...(store.drafts[d.id] ?? []), ...sent];
}

/** Route 8 as the write sample serves it: Phase 1's document plus `questionList`. */
function detailOf(f: MockFile, b: MockBusiness): DocumentDetail {
    return docsStoreApi.detail(f, b);
}

function staffDoc(d: DocumentDetail): StaffDocument {
    const { threads: _threads, questionList: _questions, questionCheck: _check, ...rest } = d;
    void _threads;
    void _questions;
    void _check;
    return rest;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** "2026-10-09" to "Fri 9 Oct". */
function dayWords(ymd: string): string {
    const [y, m, d] = ymd.split("-").map(Number);
    return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const trim = (s: string, max: number) => {
    const t = s.replace(/\s+/g, " ").trim();
    return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
};

/* ------------------------------------------------------------------------- *
 * Catalogue facts the write sample serves
 * ------------------------------------------------------------------------- */

const INPUTS: Record<string, SkillInput[]> = {
    "Answer Plan": [
        { key: "contract", label: "The signed plan and its codes", from: "Contract" },
        { key: "questions", label: "The client's 50 questions", from: "Visibility" },
        { key: "scan", label: "The last full scan of the site", from: "Site" },
        { key: "onboarding_form", label: "The onboarding form's answers", from: "Onboarding" },
        { key: "knowledge", label: "What we know about the business", from: "Knowledge" },
    ],
    "GBP Pack": [
        // S4 rides along with S2 now, so the form gives the business's details
        // as well as the outlet's.
        { key: "onboarding_form", label: "The outlet's hours, phone and address, and the business's own details", from: "Onboarding" },
        { key: "knowledge", label: "What we know about the business", from: "Knowledge" },
    ],
    "Copy Pack": [
        { key: "previous_doc", label: "The approved Answer Plan", from: "Documents" },
        { key: "questions", label: "The client's 50 questions", from: "Visibility" },
        { key: "scan", label: "The pages on the site today", from: "Site" },
        { key: "knowledge", label: "What we know about the business", from: "Knowledge" },
    ],
    "Monthly Update": [
        { key: "previous_doc", label: "Last month's approved Monthly Update", from: "Documents" },
        { key: "questions", label: "The client's 50 questions", from: "Visibility" },
        { key: "scan", label: "This month's scan", from: "Site" },
    ],
};

function templates(): DocTemplate[] {
    const rows: DocTemplate[] = DOC_EXPECTED.map((e) => ({
        docType: e.docType,
        codes: [...e.codes],
        perOutlet: !!e.perOutlet,
        eachMonth: !!e.eachMonth,
        templateFileId: `1Template${e.docType.replace(/[^A-Za-z]/g, "")}Q8`,
        skill: "none" as const,
        custom: false,
        kinds: ["template", "link", "convert", "skill"],
    }));
    // The Custom row, as `?custom=true` serves it (ourteam-backend#6516).
    rows.push({ docType: "Custom", codes: [], perOutlet: false, eachMonth: false, templateFileId: null, skill: null, custom: true, kinds: ["blank", "link", "convert"] });
    return rows;
}

function targetsOf(b: MockBusiness): CreateTarget[] {
    if (!b.folder) return [];
    const root: CreateTarget = { folderId: b.folder.folderId, path: b.folder.name, root: true, sharedWithClient: false, clientEmails: [] };
    if (b.id !== SAMPLE_ID) return [root];
    return [root, { folderId: NYO_SHARED_FOLDER, path: `${b.folder.name} / Shared with ${SIEW.name}`, root: false, sharedWithClient: true, clientEmails: [SIEW.email] }];
}

/* ------------------------------------------------------------------------- *
 * The seed: this store's own records, and the Drive fixtures it adds
 * ------------------------------------------------------------------------- */

const F2 = "doc_f2_gbp_mont_kiara";
const F3 = "doc_f3_gbp_bukit_bintang";
const F7 = "doc_f7_copy_pack_home";
const F8 = "doc_f8_competitor_watch";

const F2_QUESTIONS: Array<Pick<DocQuestion, "id" | "kind" | "options" | "text">> = [
    { id: "q_f2_1", kind: "agree", options: [], text: "Please confirm Restaurant as the main category, with Peranakan restaurant and Caterer added." },
    { id: "q_f2_2", kind: "agree", options: [], text: "Are the Mont Kiara hours the same as Bukit Bintang: 11.30am to 9.30pm, closed Monday?" },
    { id: "q_f2_3", kind: "choice", options: ["Yes, show the price", "No, keep it off"], text: "Would you like the set lunch price shown in your Google posts?" },
    { id: "q_f2_4", kind: "agree", options: [], text: "Please confirm this outlet's phone number: 03-6201 8812." },
];

/** Add the write sample's Drive-shaped facts to Phase 1's fixtures (after every reseed of that store). */
function seedDriveFixtures(files: MockFile[], _businesses: MockBusiness[], nextSeq: () => number): void {
    void _businesses;
    const f2 = files.find((f) => f.id === F2);
    if (f2?.threads) {
        const questions = f2.threads.filter((t) => !/leave any comments/i.test(t.text));
        questions.slice(0, 4).forEach((t, i) => {
            const q = F2_QUESTIONS[i];
            t.question = { id: q.id, kind: q.kind, code: null, options: q.options, number: i + 1, text: q.text };
            t.text = questionCommentOf({ ...q, code: null }, i + 1);
        });
        const [q1, q2] = questions;
        const oura: MockAuthor = { email: OURA_ACCOUNT, name: "ourteam" };
        // Rev 2 re-applies this to a rev 1 store: the answers go in once.
        const answered = Boolean(q1?.replies.some((r) => r.onBehalfOf));
        // Siew Ling answered 2 in Oura yesterday: question 1 Agreed (and so resolved), question 2 Change.
        if (!answered) q1?.replies.push(
            { id: `rep_${nextSeq()}`, by: oura, text: "Agreed.", at: "@wd:-1T10:05", action: "reply", where: "oura", onBehalfOf: SIEW, via: "oura", answer: "agree" },
            { id: `rep_${nextSeq()}`, by: oura, text: "", at: "@wd:-1T10:05", action: "resolve", where: "oura", onBehalfOf: SIEW, via: "oura", answer: "agree" },
        );
        if (!answered) q2?.replies.push({
            id: `rep_${nextSeq()}`,
            by: oura,
            text: "Change: Mont Kiara closes at 9pm on weekdays, not 9.30pm. Closed Monday is right.",
            at: "@wd:-1T10:07",
            action: "reply",
            where: "oura",
            onBehalfOf: SIEW,
            via: "oura",
            answer: "change",
        });
        f2.bodyQuestionList = F2_QUESTIONS.map((q, i) => questionListLineOf({ code: null, text: q.text }, i + 1));
        f2.modifiedAt = "@wd:-1T10:07";
    }
    // F8's line 1 says G1, the name G4. Dana's note sits on that line, so Oura leaves it until it is resolved.
    const f8 = files.find((f) => f.id === F8);
    const note = "Check G1 against G4 with Angel before this line changes.";
    const held = f8?.threads?.find((t) => t.quoted === "Competitor Watch (G1)");
    if (held) held.text = note;
    else f8?.threads?.push({ id: `thr_${nextSeq()}`, by: { email: "dana@ourteam.ai", name: "Dana" }, text: note, quoted: "Competitor Watch (G1)", at: "@wd:-2T14:20", replies: [] });
}

function seedWriteStore(s: WriteStore): WriteStore {
    const sentAt = myInstant(shiftWorkingDays(myDate(new Date()), 3), 11, 0);
    F2_QUESTIONS.forEach((q) => {
        s.questionMeta[q.id] = { createdBy: "Angel", createdAt: sentAt, sentAt };
    });
    s.drafts[F7] = [
        {
            id: "q_f7_draft_1",
            code: null,
            kind: "choice",
            text: "Would you like the halal-friendly line on the homepage?",
            options: ["Yes, add it", "No, leave it off"],
            number: null,
            commentId: null,
            threadId: null,
            state: "draft",
            createdBy: "Angel",
            createdAt: myInstant(shiftWorkingDays(myDate(new Date()), 1), 16, 20),
            sentAt: null,
        },
    ];
    const reviewAt = myInstant(shiftWorkingDays(myDate(new Date()), 2), 9, 40);
    s.messages[SAMPLE_ID] = [
        {
            id: "msg_seed_1",
            kind: "doc_review",
            to: [{ name: SIEW.name, email: SIEW.email }],
            cc: [],
            subject: "4 questions on your Mont Kiara Google profile",
            body: "Hi Siew Ling,\n\n4 questions are waiting for you in your Mont Kiara GBP Pack. You can answer in the document, or in Oura under Documents.\n\nAngel\nourteam",
            documentIds: [F2],
            questionCount: 4,
            replyBy: null,
            sentAt: reviewAt,
            sentBy: "Angel",
        },
    ];
    return s;
}

/* ------------------------------------------------------------------------- *
 * The fake server
 * ------------------------------------------------------------------------- */

function validQuestion(body: QuestionIn | QuestionPatchIn, partial: boolean): DocsResult<null> {
    if (!partial || body.text !== undefined) {
        if (!body.text || !body.text.trim()) return refuse("question_text_required", 400);
    }
    if (body.kind === "choice" && (body.options ?? []).filter((o) => o.trim()).length < 2) return refuse("question_options_required", 400);
    if (body.code && !CODE_RE.test(body.code.trim())) return refuse("unknown_code", 400, `Oura doesn't know ${body.code}.`, { badCodes: [body.code] });
    return ok(null);
}

/** The comment for a question, as it reads in the doc (clientDocsRules, the same words the editor previews). */
function questionText(q: { code: string | null; kind: DocQuestion["kind"]; options: string[]; text: string }, n: number): string {
    return questionCommentOf(q, n);
}

/** Rewrite the numbered list Oura keeps in the body from the questions on record. */
function writeBodyList(f: MockFile): void {
    f.bodyQuestionList = (f.threads ?? [])
        .filter((t) => t.question)
        .sort((x, y) => (x.question?.number ?? 0) - (y.question?.number ?? 0))
        .map((t) => questionListLineOf({ code: t.question?.code ?? null, text: t.question?.text ?? "" }, t.question?.number ?? 0));
}

/** The 3 counts that must agree after Send to the doc; null when Oura sent no question to this doc. */
function questionCheckOf(f: MockFile | null): QuestionCheck | null {
    const sent = (f?.threads ?? []).filter((t) => t.question).length;
    if (!f || (sent === 0 && !f.bodyQuestionList?.length)) return null;
    return { inOura: sent, inList: f.bodyQuestionList?.length ?? null, comments: f.threads === null ? null : sent };
}

/** The docs a message covers, each shared with the client. */
function sharedDocs(documentIds: string[]): DocsResult<Array<{ f: MockFile; b: MockBusiness; d: DocumentDetail }>> {
    if (documentIds.length === 0) return refuse("not_shared", 409, "Pick at least 1 shared document.");
    const out: Array<{ f: MockFile; b: MockBusiness; d: DocumentDetail }> = [];
    for (const id of documentIds) {
        const h = hit(id);
        if (!h) return notFound();
        const d = detailOf(h.f, h.b);
        if (!d.sharing.clientCanSee) return refuse("not_shared", 409);
        out.push({ ...h, d });
    }
    return ok(out);
}

/** The email is from Angel at oura@ (the sender line), so it is signed by Angel too; who sent it is on record apart. */
const SIGNED = "Angel";

function draftMessage(businessId: string, kind: DocMessageKind, documentIds: string[]): DocsResult<DocMessageDraft> {
    const docs = sharedDocs(documentIds);
    if (!docs.ok) return docs;
    const list = docs.data;
    const first = list[0];
    if (!first || first.b.id !== businessId) return notFound("That document is not this client's.");
    const client = docsStoreApi.clientOf(first.f, first.b);
    const waiting = list.reduce((n, x) => n + (x.d.questions?.waitingOnClient ?? 0), 0);
    const set = list.map((x) => x.d.replyBy).filter((r): r is string => !!r).sort()[0] ?? null;
    // A reply-by that has passed is never offered again: a resend proposes a new one, 5 working days on.
    const replyBy = set && set < today() ? defaultReplyBy(now()) : set;
    // The client's own names for the docs, as their list in Oura calls them.
    const titles = list.map((x) => merchantDocName(docsStoreApi.title(x.f)));
    const links = list.map((x, i) => `${titles[i]}: ${x.d.url}`).join("\n");
    // The questions still waiting, each by its number and words, so the chaser names them.
    const waitingLines = list
        .flatMap((x, i) =>
            x.d.threads
                .filter((t) => t.kind === "question" && !t.internal && (t.state === "waiting_on_client" || t.state === "late"))
                .map((t) => {
                    const words = (threadOf(x.f, t.id)?.question?.text ?? t.text).replace(/^\s*\[[^\]]*\]\s*/, "").replace(/^(?:Q\d+\s+|\d+\.\s+)/, "");
                    return `- ${list.length > 1 ? `${titles[i]}, ` : ""}${t.number ? `Q${t.number}: ` : ""}${trim(words, 120)}`;
                }),
        )
        .join("\n");
    const firstName = client.name;
    const docsWord = list.length === 1 ? `your ${titles[0]}` : `these ${list.length} documents`;
    const by = replyBy ? ` Please reply by ${dayWords(replyBy)}.` : "";
    const subject = kind === "doc_review"
        ? `${plural(waiting, "question")} to review${list.length === 1 ? ` in your ${titles[0]}` : ""}${replyBy ? `, reply by ${dayWords(replyBy)}` : ""}`
        : `A reminder: ${plural(waiting, "question")} waiting${list.length === 1 ? ` in your ${titles[0]}` : ""}`;
    const body = kind === "doc_review"
        ? `Hi ${firstName},\n\n${plural(waiting, "question")} ${waiting === 1 ? "is" : "are"} waiting for you in ${docsWord}:\n\n${links}\n\nYou can answer in the document, or in Oura under Documents.${by}\n\n${SIGNED}\nourteam`
        : `Hi ${firstName},\n\nA reminder that ${plural(waiting, "question")} ${waiting === 1 ? "is" : "are"} still waiting for you. A short reply is enough.${by}\n\n${waitingLines ? `${waitingLines}\n\n` : ""}${links}\n\n${SIGNED}\nourteam`;
    return ok({ kind, to: [{ name: client.name, email: client.email }], cc: [], subject, body, documentIds, questionCount: waiting, replyBy });
}

function approvalRecordOf(d: DocumentDetail, f: MockFile): ApprovalRecord {
    const rows: ApprovalRecordRow[] = [];
    for (const t of d.threads) {
        if (t.kind !== "question" || !t.resolved || !t.resolvedBy || !t.resolvedAt) continue;
        const resolveIdx = t.posts.map((p) => p.action).lastIndexOf("resolve");
        const before = t.posts.slice(0, resolveIdx < 0 ? t.posts.length : resolveIdx);
        const clientPost = [...before].reverse().find((p) => p.author.side === "client" && p.action === "reply" && p.text.trim()) ?? null;
        const resolvePost = resolveIdx >= 0 ? t.posts[resolveIdx] : null;
        const stored = f.threads?.find((x) => x.id === t.id)?.replies.find((r) => r.id === resolvePost?.id);
        rows.push({
            threadId: t.id,
            questionId: t.questionId ?? null,
            number: t.number ?? null,
            question: f.threads?.find((x) => x.id === t.id)?.question?.text ?? t.text,
            clientWords: clientPost ? clientPost.text : t.resolvedBy.side === "client" ? "Resolved it" : null,
            clientVia: clientPost ? clientPost.via ?? "doc" : t.resolvedBy.side === "client" ? (resolvePost?.via ?? "doc") : null,
            resolvedBy: { name: t.resolvedBy.name, side: t.resolvedBy.side },
            where: resolvePost?.where === "oura" ? "in_oura" : "in_doc",
            at: t.resolvedAt,
            docVersion: stored?.docVersion ?? d.version,
            flag: t.resolvedAgreed === false ? "unagreed" : null,
        });
    }
    rows.sort((x, y) => (x.number ?? 999) - (y.number ?? 999) || x.at.localeCompare(y.at));
    return { documentId: d.id, rows, unagreed: rows.filter((r) => r.flag).length };
}

/**
 * Lane 4: what the AI would notice in our own sample docs, from their
 * fixtures (nyonya-table only): kept while the doc is not approved.
 */
const SAMPLE_MISTAKES: Record<string, string[]> = {
    doc_f1_answer_plan: [
        "Page 2 still says “Open daily, 11.30am to 9.30pm”. Siew Ling says the restaurant closes on Mondays from next month.",
        "The rivals list still names Kim Choo, which Siew Ling says is a shop, not a restaurant.",
    ],
    [F2]: ["The hours line says Mont Kiara closes at 9.30pm. Siew Ling says it closes at 9pm on weekdays."],
};

function summaryOf(d: DocumentDetail): DocSummary {
    const q = d.questions;
    const asked = d.threads.filter((t) => t.kind === "question");
    const label = (t: DocThread) => (t.kind === "question" ? `Question ${t.number ?? asked.indexOf(t) + 1}` : "The client's comment");
    const needsUs = d.threads.filter((t) => t.state === "needs_us");
    const waiting = d.threads.filter((t) => t.kind === "question" && (t.state === "waiting_on_client" || t.state === "late"));
    const openPoints = [
        ...needsUs.map((t) => `${label(t)}: ${trim(lastClientWord(t)?.text ?? t.text, 100)}`),
        // The question's own words: no "[S2-MK] Q3" in front, no "Reply AGREED, or ..." after.
        ...waiting.slice(0, 3).map((t) => `${label(t)} waits on the client: ${trim(t.text.replace(/^\s*(\[[^\]]*\]\s*)?(Q\d+\s+|\d+\.\s*)?/, "").replace(/\s+Reply\b[\s\S]*$/, ""), 80)}`),
    ];
    const mistakes = [
        ...d.threads.filter((t) => t.resolvedAgreed === false).map((t) => `${label(t)} was resolved without the client's agreement.`),
        ...d.threads.filter((t) => t.internal && !t.resolved).map((t) => `An internal note is still in the doc: ${trim(t.text, 80)}`),
        ...(d.state !== "approved" ? SAMPLE_MISTAKES[d.id] ?? [] : []),
    ];
    const line = !d.sharing.clientCanSee
        ? `Not shared yet. ${plural(q?.out ?? 0, "question")} in it.`
        : q
            ? waiting.length + needsUs.length === 0
                ? q.approved > 0 ? `All ${plural(q.approved, "question")} approved. Nothing is open.` : "No questions in it yet."
                : `${[
                    waiting.length > 0 && `${plural(waiting.length, "question")} waiting on the client`,
                    needsUs.length > 0 && `${needsUs.length} ${needsUs.length === 1 ? "needs" : "need"} us`,
                    q.approved > 0 && `${q.approved} approved`,
                ].filter(Boolean).join(", ")}.`
            : "Comments not read yet.";
    const nextStep = !d.sharing.clientCanSee
        ? "Read it, then share it with the client."
        : needsUs.length
            ? needsUs.length === 1 ? "Reply to the answer that needs us." : `Reply to the ${needsUs.length} answers that need us.`
            : waiting.length
                ? d.replyBy
                    ? d.replyBy < today()
                        ? `Chase: reply was due ${dayWords(d.replyBy)}.`
                        : `Wait for the client's reply by ${dayWords(d.replyBy)}.`
                    : "Wait for the client's reply."
                : d.state === "approved"
                    ? "File the approved PDF."
                    : "Nothing is open.";
    return { documentId: d.id, line, openPoints, mistakes, nextStep, madeAt: now(), madeFrom: d.modifiedAt, label: "AI", cap: SUMMARY_CAP };
}

store = load();

// Another tab wrote this store (a message sent, a PDF filed): read it again. Phase 1's store tells the screens.
if (typeof window !== "undefined" && DOCS_WRITE_SAMPLE_SERVES) {
    window.addEventListener("storage", (e) => {
        if (e.key === STORE_KEY) store = load();
    });
}

/* ------------------------------------------------------------------------- *
 * The client email for a finished doc (sample only; the server writes, renders and checks it)
 * ------------------------------------------------------------------------- */

const CLIENT_EMAIL_PURPOSES: { key: ClientEmailPurpose; label: string }[] = [
    { key: "review", label: "Send for review" },
    { key: "follow_up", label: "Follow up" },
];

function clientEmailRefusal(d: StaffDocument): "not_done" | "not_shared" | null {
    if (d.state !== "done") return "not_done";
    return d.sharing.clientCanSee ? null : "not_shared";
}

const esc = (t: string) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** A rough stand-in for the server's renderer, so the sample shows something like the real email. */
function sampleHtml(body: string): string {
    const inline = (t: string) =>
        esc(t)
            .replace(/\*\*\[([^\]]+)\]\((https:\/\/[^\s)]+)\)\*\*/g, '<strong><a href="$2">$1</a></strong>')
            .replace(/\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>')
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    return body
        .split(/\n\s*\n/)
        .filter((b) => b.trim())
        .map((b) => {
            const lines = b.split("\n");
            const items = lines.filter((l) => /^\s*(-|\d+\.)\s+/.test(l));
            const head = lines.filter((l) => !/^\s*(-|\d+\.)\s+/.test(l));
            const tag = items.length && /^\s*\d/.test(items[0]) ? "ol" : "ul";
            return (
                (head.length ? `<p>${head.map(inline).join("<br>")}</p>` : "") +
                (items.length ? `<${tag}>${items.map((l) => `<li>${inline(l.replace(/^\s*(-|\d+\.)\s+/, ""))}</li>`).join("")}</${tag}>` : "")
            );
        })
        .join("");
}

function sampleChecks(body: string, to: DocMessagePerson[]): ClientEmailCheck[] {
    const first = to[0]?.name.trim().split(" ")[0];
    const want = first ? (to.length > 1 ? `Hi ${first} and team,` : `Hi ${first},`) : "Hi there,";
    const links = body.match(/https?:\/\/[^\s)\]]+/g) ?? [];
    const bullets = body.split("\n").filter((l) => /^\s*-\s+/.test(l)).length;
    return [
        { id: "one_link", ok: links.length === 1, level: "block", text: "Exactly one link, and it is this document's" },
        { id: "greeting", ok: body.split("\n")[0].trim() === want, level: "block", text: `It opens with "${want}"` },
        { id: "signature", ok: body.trim().endsWith("Angel\nourteam"), level: "block", text: "It is signed Angel, ourteam" },
        { id: "highlights", ok: bullets >= 3 && bullets <= 4, level: "warn", text: "3 to 4 highlights from the document" },
    ];
}

export const docsWriteMock = {
    getWriteStatus: () =>
        read<DocsWriteStatus>(() => ok(writeStatus())),

    getTemplates: () => read<DocTemplate[]>(() => ok(templates())),

    getTargets: (businessId: string) =>
        read<CreateTarget[]>(() => {
            const b = docsStoreApi.business(businessId);
            return ok(b ? targetsOf(b) : []);
        }),

    getSkillLibrary: () =>
        read<SkillLibrary>(() =>
            ok({
                slots: DOC_EXPECTED.map((e) => ({
                    docType: e.docType,
                    codes: [...e.codes],
                    status: "none" as const,
                    version: null,
                    addedAt: null,
                    addedBy: null,
                    inputs: INPUTS[e.docType] ?? [],
                    costPerRunUsd: null,
                    runs: [],
                })),
                cap: SKILL_CAP,
            }),
        ),

    createDocument: (businessId: string, body: CreateDocIn) =>
        respond<CreateDocResult>(() => {
            const b = docsStoreApi.business(businessId);
            if (!b?.folder) return notFound("Link the client's folder first.");
            // A custom doc type's label stands in for `docType` (ourteam-backend#6516).
            if (body.customType != null) {
                const problem = customLabelProblem(body.customType);
                if (problem) return refuse("custom_type_invalid", 400, problem);
                body = { ...body, docType: customLabel(body.customType) };
            } else if (!DOC_EXPECTED.find((e) => e.docType === body.docType)) return refuse("doc_type_unknown", 400);
            const target = targetsOf(b).find((t) => t.folderId === (body.folderId ?? b.folder?.folderId));
            if (!target) return notFound("That folder is not in the client's folder.");
            if (target.sharedWithClient) return refuse("folder_shared_with_client", 409);
            const bad = body.codes.filter((c) => !BASE_CODE_RE.test(c.trim()));
            if (bad.length || body.codes.length === 0) return refuse("unknown_code", 400, bad.length ? `Oura doesn't know ${bad.join(", ")}.` : "Pick at least 1 code.", { badCodes: bad });
            const outlet = body.outlet?.trim() || null;
            if (outlet !== null && !/^[A-Z]{2,4}$/.test(outlet)) return refuse("outlet_invalid", 400);
            if (body.kind === "skill") return refuse("skill_not_added", 409);

            // Never offered twice, whichever way it is made: a doc already covering these codes that
            // the client cannot see yet. The file being converted or linked is the one made, not a second.
            const board = docsStoreApi.board(b);
            const source = body.kind === "convert" ? body.fileId ?? null : body.kind === "link" ? /\/document\/d\/([A-Za-z0-9_-]{10,})/.exec(body.linkUrl ?? "")?.[1] ?? null : null;
            const isSource = (d: { id: string; url?: string | null }) => Boolean(source && (d.id === source || d.url?.includes(source)));
            const existing = board.documents.find(
                (d) => !d.notClientDoc && !d.sharing.clientCanSee && !isSource(d) && body.codes.every((c) => d.codes.includes(c)) && (d.outlet ?? null) === outlet,
            );
            if (existing) return refuse("doc_exists_unshared", 409, undefined, { existingId: existing.id });

            let driveFileId = `1Oura${Date.now().toString(36)}${store.seq}Dc7`;
            if (body.kind === "link") {
                const m = /docs\.google\.com\/document\/d\/([A-Za-z0-9_-]{10,})/.exec(body.linkUrl ?? "");
                if (!m) return refuse("link_not_a_doc", 400);
                driveFileId = m[1];
            }
            if (body.kind === "convert") {
                const word = docsStoreApi.files(b.id).find((f) => f.id === body.fileId || f.driveFileId === body.fileId);
                if (!word || !WORD_MIMES.includes(word.mimeType)) return refuse("file_not_word", 400);
            }

            const labels = codeChipsOf(body.codes, outlet).map((c) => c.label);
            const title = body.title?.trim() || `${body.docType} (${joinCodes(labels)})`;
            const id = nextKey("doc");
            const f: MockFile = {
                id,
                driveFileId,
                businessId: b.id,
                folderId: b.folder.folderId,
                // Bare codes and the outlet in its own Location segment: the
                // name no longer carries an outlet on a code.
                name: docNameOf({ client: b.name, docType: body.docType, codes: body.codes, location: outlet, version: "1.0", date: body.nameDate ?? today() }),
                nameDate: null,
                pageTitle: `${title}\n${versionLineOf(b.name, "1.0")}`,
                mimeType: GDOC,
                owner: OURA_ACCOUNT,
                createdAt: now(),
                modifiedAt: now(),
                trashed: false,
                permissions: [{ type: "user", email: OURA_ACCOUNT, name: "Angel", role: "owner" }],
                sharedAt: null,
                pick: null,
                notClientDoc: false,
                replyBy: null,
                threads: [],
                commentsError: false,
            };
            docsStoreApi.addFile(f);
            return ok({ document: staffDoc(detailOf(f, b)), run: null });
        }),

    addQuestion: (documentId: string, body: QuestionIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const v = validQuestion(body, false);
            if (!v.ok) return v;
            const q: DocQuestion = {
                id: nextKey("q"),
                code: body.code?.trim() || null,
                kind: body.kind,
                text: body.text.trim(),
                options: body.kind === "choice" ? (body.options ?? []).map((o) => o.trim()).filter(Boolean) : [],
                number: null,
                commentId: null,
                threadId: null,
                state: "draft",
                createdBy: staff(),
                createdAt: now(),
                sentAt: null,
            };
            store.drafts[documentId] = [...(store.drafts[documentId] ?? []), q];
            return ok(detailOf(h.f, h.b));
        }),

    updateQuestion: (documentId: string, questionId: string, patch: QuestionPatchIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const v = validQuestion(patch, true);
            if (!v.ok) return v;
            const draft = (store.drafts[documentId] ?? []).find((q) => q.id === questionId);
            if (draft) {
                Object.assign(draft, {
                    ...(patch.text !== undefined ? { text: patch.text.trim() } : {}),
                    ...(patch.kind ? { kind: patch.kind } : {}),
                    ...(patch.code !== undefined ? { code: patch.code?.trim() || null } : {}),
                    ...(patch.options ? { options: patch.options.map((o) => o.trim()).filter(Boolean) } : {}),
                });
                if (draft.kind !== "choice") draft.options = [];
                return ok(detailOf(h.f, h.b));
            }
            const t = h.f.threads?.find((x) => x.question?.id === questionId);
            if (!t?.question) return notFound("That question is no longer in the doc.");
            const rec = t.question;
            if (patch.text !== undefined) rec.text = patch.text.trim();
            if (patch.kind) rec.kind = patch.kind;
            if (patch.code !== undefined) rec.code = patch.code?.trim() || null;
            if (patch.options) rec.options = patch.options.map((o) => o.trim()).filter(Boolean);
            if (rec.kind !== "choice") rec.options = [];
            // The comment in the doc is edited too, and its line in the body list.
            t.text = questionText(rec, rec.number ?? 0);
            writeBodyList(h.f);
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    deleteQuestion: (documentId: string, questionId: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const drafts = store.drafts[documentId] ?? [];
            if (drafts.some((q) => q.id === questionId)) {
                store.drafts[documentId] = drafts.filter((q) => q.id !== questionId);
                return ok(detailOf(h.f, h.b));
            }
            const t = h.f.threads?.find((x) => x.question?.id === questionId);
            if (!t || !h.f.threads) return notFound("That question is no longer in the doc.");
            const answered = t.replies.some((r) => r.onBehalfOf || !isOurEmail(r.by.email));
            if (answered) return refuse("question_answered", 409);
            h.f.threads = h.f.threads.filter((x) => x !== t);
            writeBodyList(h.f);
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    sendQuestions: (documentId: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const drafts = store.drafts[documentId] ?? [];
            if (drafts.length === 0) return refuse("questions_none_to_send", 409);
            const threads = (h.f.threads ??= []);
            // New questions follow every question already in the doc, Oura's or written by hand, as the drawer numbers them.
            const asked = detailOf(h.f, h.b).threads.filter((t) => t.kind === "question").length;
            let n = Math.max(asked, threads.reduce((max, t) => Math.max(max, t.question?.number ?? 0), 0));
            for (const q of drafts) {
                n += 1;
                threads.push({
                    id: docsStoreApi.nextId("thr"),
                    by: docsStoreApi.ouraAuthor(staff()),
                    text: questionText(q, n),
                    quoted: null,
                    at: now(),
                    replies: [],
                    question: { id: q.id, kind: q.kind, code: q.code, options: q.options, number: n, text: q.text },
                });
                store.questionMeta[q.id] = { createdBy: q.createdBy, createdAt: q.createdAt, sentAt: now() };
            }
            store.drafts[documentId] = [];
            // The numbered list Oura keeps in the body.
            writeBodyList(h.f);
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    setLines: (documentId: string, body: DocLinesIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const [line1 = "", line2 = "", ...rest] = (h.f.pageTitle ?? "").split("\n");
            const title = body.titleLine !== undefined ? body.titleLine.trim() : line1;
            const version = body.versionLine !== undefined ? body.versionLine.trim() : line2;
            if (!title) return refuse("answer_invalid", 400, "The title line can't be empty.");
            // Never rewrite text an open comment sits on (B16).
            const open = detailOf(h.f, h.b).threads.filter((t) => !t.resolved && t.quoted?.trim());
            const under = (line: string) => open.some((t) => line.trim() && (line.includes(t.quoted?.trim() ?? "") || (t.quoted ?? "").includes(line.trim())));
            if ((title !== line1 && under(line1)) || (version !== line2 && under(line2))) return refuse("line_under_open_comment", 409);
            h.f.pageTitle = [title, version, ...rest].filter((x, i) => i === 0 || x).join("\n");
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    /**
     * The sample store never fails a fill (a template create is filled at once),
     * so there is nothing here to run again: the answer is the server's own
     * `fill_not_started`, which is also what a real doc made by link answers.
     */
    rerunFill: (documentId: string) =>
        respond<RerunFillResult>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            return refuse("fill_not_started", 409);
        }, false),

    /**
     * The sample has no skill added for any doc type (`getTemplates` says `none`), so a
     * re-run is what the server answers for that: `skill_not_added`. No sample run exists.
     */
    rerunSkill: (documentId: string) =>
        respond<SkillRun>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            return refuse("skill_not_added", 409);
        }, false),

    rename: (documentId: string, body: RenameIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = detailOf(h.f, h.b);
            let name = body.name?.trim() || "";
            if (name) {
                // The format Oura writes now. A doc still carrying an older
                // name reads fine everywhere, but a rename writes the new one.
                if (parseDocName(name).format !== "oura") return refuse("name_format_invalid", 400);
            } else {
                name = docNameOf({
                    client: h.b.name,
                    docType: d.docType ?? "Document",
                    codes: d.codes,
                    location: d.outlet,
                    version: d.version ?? "1.0",
                    date: today(),
                });
            }
            h.f.name = name;
            h.f.nameDate = null;
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    shareCheck: (documentId: string) =>
        read<ShareCheck>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            return ok(shareCheckOf(h.f, h.b));
        }),

    share: (documentId: string, emails: string[], alreadySent = false) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const list = Array.from(new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean)));
            if (list.length === 0) return refuse("share_emails_required", 400);
            const badEmail = list.find((e) => !EMAIL_RE.test(e));
            if (badEmail) return refuse("email_invalid", 400, `${badEmail} isn't an email address.`);
            if (list.some((e) => isOurEmail(e))) return refuse("email_invalid", 400, "Share to the client's address, not an ourteam one.");
            const check = shareCheckOf(h.f, h.b);
            if (!check.ok) return refuse("share_check_failed", 409);
            const client = docsStoreApi.clientOf(h.f, h.b);
            for (const email of list) {
                if (!h.f.permissions.some((p) => p.email?.toLowerCase() === email)) {
                    h.f.permissions.push({ type: "user", email, name: email === client.email ? client.name : null, role: "commenter" });
                }
            }
            const shared = detailOf(h.f, h.b);
            if (!h.f.sharedAt && alreadySent) {
                // Sent to the client before Oura (backend #6553): recorded, no bell. The
                // mock keeps no Done day, so it is dated now.
                h.f.sharedAt = now();
            } else if (!h.f.sharedAt) {
                h.f.sharedAt = now();
                // Bell only: no email from Oura or Google (B17).
                // In the client's words, as their list names it ("Page wording, Home").
                //
                // "to review" ONLY WHEN SOMETHING IS ASKED. With no questions
                // on it the client's own page files the doc under "Nothing to
                // answer", so a bell asking them to review it -- and giving
                // them a reply-by -- contradicts the page it links to (#4051).
                docsStoreApi.notice({
                    audience: "client",
                    businessId: h.b.id,
                    title: `${sharedNoticeLead(shared.questions?.out ?? null)}: ${merchantDocName(docsStoreApi.title(h.f))}`,
                    body: "",
                    link: `/oura/decisions?doc=${h.f.id}`,
                });
            }
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    fixShare: (documentId: string, code: ShareFixCode) =>
        respond<ShareCheck>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            if (code === "link_sharing") {
                h.f.permissions = h.f.permissions.filter((p) => p.type !== "anyone");
            } else {
                // Every outside editor becomes a commenter: comment-only is the rule.
                h.f.permissions = h.f.permissions.map((p) =>
                    p.role === "writer" && p.type !== "anyone" && !(p.email && isOurEmail(p.email)) ? { ...p, role: "commenter" as const } : p,
                );
            }
            touch(h.f);
            return ok(shareCheckOf(h.f, h.b));
        }),

    messageDraft: (businessId: string, kind: DocMessageKind, documentIds: string[]) => read<DocMessageDraft>(() => draftMessage(businessId, kind, documentIds)),

    sendMessage: (businessId: string, body: DocMessageIn) =>
        respond<SentDocMessage>(() => {
            const docs = sharedDocs(body.documentIds);
            if (!docs.ok) return docs;
            if (body.to.length === 0) return refuse("share_emails_required", 400, "Add who the email goes to.");
            const bad = [...body.to, ...body.cc].find((p) => !EMAIL_RE.test(p.email.trim()));
            if (bad) return refuse("email_invalid", 400, `${bad.email} isn't an email address.`);
            if (!body.subject.trim() || !body.body.trim()) return refuse("message_empty", 400);
            const sent: SentDocMessage = { ...body, id: nextKey("msg"), sentAt: now(), sentBy: staff() };
            // The date the email gives is the doc's reply-by from now on.
            if (body.replyBy) for (const x of docs.data) if (!x.f.replyBy || x.f.replyBy < body.replyBy) x.f.replyBy = body.replyBy;
            store.messages[businessId] = [sent, ...(store.messages[businessId] ?? [])];
            return ok(sent);
        }),

    listMessages: (businessId: string) => read<SentDocMessage[]>(() => ok(store.messages[businessId] ?? [])),

    clientEmail: (documentId: string) =>
        read<ClientEmailState>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = staffDoc(detailOf(h.f, h.b));
            const no = clientEmailRefusal(d);
            const last = (store.messages[h.b.id] ?? []).find((m) => m.kind === "doc_delivery" && m.documentIds.includes(documentId)) ?? null;
            return ok({
                eligible: !no,
                refusal: no ? { code: no, message: no === "not_done" ? "Mark the doc Done first." : "Share the doc with the client first." } : null,
                purposes: CLIENT_EMAIL_PURPOSES,
                purpose: last ? "follow_up" : "review",
                draft: null,
                last,
                cap: { monthCapUsd: 20, alertAtUsd: 16, spentUsd: null, stopped: false },
            });
        }),

    draftClientEmail: (documentId: string, options: { purpose?: ClientEmailPurpose }) =>
        read<ClientEmailDraft>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = staffDoc(detailOf(h.f, h.b));
            const no = clientEmailRefusal(d);
            if (no) return refuse(no, 409);
            const to = h.f.permissions
                .filter((p) => p.type === "user" && p.email && !isOurEmail(p.email) && p.role !== "owner")
                .map((p) => ({ name: p.name ?? "", email: p.email ?? "" }));
            const first = to[0]?.name.split(" ")[0];
            const title = d.docType ?? "document";
            const body = [
                first ? (to.length > 1 ? `Hi ${first} and team,` : `Hi ${first},`) : "Hi there,",
                `We've prepared the ${title} for your review.`,
                `**[${title}](${d.url})**`,
                "The document covers:\n- A sample highlight Oura read from the doc\n- Another one, in plain words\n- A third, so there are 3",
                "**What we need from you:**\n1. Could you confirm the opening hours?",
                "Please leave your answers, approvals or comments directly in the document.",
                "Thank you!\n\nCheers,\nAngel\nourteam",
            ].join("\n\n");
            const subject = `${title} for Review – ${h.b.name}`;
            return ok({
                draftId: nextKey("draft"),
                purpose: options.purpose ?? "review",
                madeFrom: d.modifiedAt,
                madeAt: now(),
                to,
                cc: [],
                subject,
                body,
                html: sampleHtml(body),
                checks: sampleChecks(body, to),
                dropped: 0,
                label: "AI",
            });
        }),

    testClientEmail: (documentId: string) => read<{ to: string }>(() => (hit(documentId) ? ok({ to: "user-notif@ourteam.ai" }) : notFound())),

    previewClientEmail: (documentId: string, body: { subject: string; body: string; to: DocMessagePerson[] }) =>
        read<ClientEmailPreview>(() => (hit(documentId) ? ok({ html: sampleHtml(body.body), checks: sampleChecks(body.body, body.to) }) : notFound())),

    sendClientEmail: (documentId: string, body: ClientEmailSendIn) =>
        respond<SentDocMessage>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const no = clientEmailRefusal(staffDoc(detailOf(h.f, h.b)));
            if (no) return refuse(no, 409);
            if (sampleChecks(body.body, body.to).some((c) => c.level === "block" && !c.ok)) return refuse("check_failed", 400);
            const sent: SentDocMessage = {
                kind: "doc_delivery",
                to: body.to,
                cc: body.cc,
                subject: body.subject,
                body: body.body,
                documentIds: [documentId],
                questionCount: 0,
                replyBy: null,
                id: nextKey("msg"),
                sentAt: now(),
                sentBy: staff(),
            };
            store.messages[h.b.id] = [sent, ...(store.messages[h.b.id] ?? [])];
            return ok(sent);
        }),

    reply: (documentId: string, threadId: string, text: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That thread is no longer in the doc.");
            if (!text.trim()) return refuse("reply_required", 400);
            t.replies.push(ourReply(text.trim()));
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    resolve: (documentId: string, threadId: string, body: ResolveIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That thread is no longer in the doc.");
            const computed = detailOf(h.f, h.b).threads.find((x) => x.id === threadId);
            if (!computed) return notFound("That thread is no longer in the doc.");
            if (computed.resolved) return refuse("thread_resolved", 409);
            const agreed = resolveCarriesAgreement("us", lastClientWord(computed), computed.questionKind ?? null);
            if (!agreed && !body.confirmNotAgreed && computed.kind === "question") return refuse("client_not_agreed", 409);
            t.replies.push(ourReply("", "resolve"));
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    reopen: (documentId: string, threadId: string, reason?: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That thread is no longer in the doc.");
            const computed = detailOf(h.f, h.b).threads.find((x) => x.id === threadId);
            if (!computed?.resolved) return refuse("thread_open", 409);
            t.replies.push(ourReply(reason?.trim() ?? "", "reopen"));
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    resolveAgreed: (documentId: string, body: ResolveAgreedIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = detailOf(h.f, h.b);
            const pick = body.threadIds ? new Set(body.threadIds) : null;
            const agreed = d.threads.filter((t) => t.kind === "question" && !t.resolved && (!pick || pick.has(t.id)) && resolveCarriesAgreement("us", lastClientWord(t), t.questionKind ?? null));
            for (const t of agreed) threadOf(h.f, t.id)?.replies.push(ourReply("", "resolve"));
            if (agreed.length) touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    editPost: (documentId: string, threadId: string, postId: string, text: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That thread is no longer in the doc.");
            if (!text.trim()) return refuse("reply_required", 400);
            if (postId === `${threadId}:open`) {
                if (!isOurEmail(t.by.email)) return refuse("not_our_post", 409);
                if (t.question) {
                    t.question.text = text.trim();
                    t.text = questionText(t.question, t.question.number ?? 0);
                    writeBodyList(h.f);
                } else {
                    t.text = text.trim();
                }
            } else {
                const r = t.replies.find((x) => x.id === postId);
                if (!r) return notFound("That post is no longer in the doc.");
                if (!isOurEmail(r.by.email) || r.onBehalfOf || r.action !== "reply") return refuse("not_our_post", 409);
                r.text = text.trim();
                r.editedAt = now();
            }
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    deletePost: (documentId: string, threadId: string, postId: string) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t || !h.f.threads) return notFound("That thread is no longer in the doc.");
            if (postId === `${threadId}:open`) {
                if (!isOurEmail(t.by.email)) return refuse("not_our_post", 409);
                if (t.replies.some((r) => r.onBehalfOf || !isOurEmail(r.by.email))) return refuse("question_answered", 409);
                h.f.threads = h.f.threads.filter((x) => x !== t);
            } else {
                const r = t.replies.find((x) => x.id === postId);
                if (!r) return notFound("That post is no longer in the doc.");
                if (!isOurEmail(r.by.email) || r.onBehalfOf || r.action !== "reply") return refuse("not_our_post", 409);
                t.replies = t.replies.filter((x) => x !== r);
            }
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    cleanUpPlaceholders: (businessId: string, body: PlaceholderCleanUpIn) =>
        respond<PlaceholderCleanUp>(() => {
            const b = docsStoreApi.business(businessId);
            if (!b) return notFound("No such client.");
            const dryRun = body.dryRun !== false;
            const documents: PlaceholderCleanUp["documents"] = [];
            let found = 0;
            for (const f of docsStoreApi.files(businessId)) {
                const d = detailOf(f, b);
                const ids = new Set(
                    d.threads.filter((t) => t.kind === "placeholder" && t.openedBy.side === "us" && !t.posts.some((p) => p.author.side === "client")).map((t) => t.id),
                );
                if (ids.size === 0) continue;
                found += ids.size;
                documents.push({ documentId: f.id, title: docsStoreApi.title(f).split("\n")[0], count: ids.size });
                if (!dryRun && f.threads) {
                    f.threads = f.threads.filter((t) => !ids.has(t.id));
                    touch(f);
                }
            }
            const out: PlaceholderCleanUp = { dryRun, found, removed: dryRun ? 0 : found, documents };
            if (!dryRun) store.cleanUps[businessId] = out;
            return ok(out);
        }),

    logAnswer: (documentId: string, threadId: string, body: LogAnswerIn) =>
        respond<DocumentDetail>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That thread is no longer in the doc.");
            if (!body.text.trim()) return refuse("answer_invalid", 400, "Write what the client said.");
            const said = Date.parse(body.saidAt);
            if (!Number.isFinite(said)) return refuse("answer_invalid", 400, "Pick when they said it.");
            if (said > Date.now() + 60_000) return refuse("said_at_in_future", 400);
            const client = docsStoreApi.clientOf(h.f, h.b);
            t.replies.push(
                onBehalfReply(client, body.text.trim(), { via: body.via, answer: body.answer ?? null, loggedBy: staff(), saidAt: new Date(said).toISOString() }),
            );
            touch(h.f);
            return ok(detailOf(h.f, h.b));
        }),

    approvalRecord: (documentId: string) =>
        read<ApprovalRecord>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            return ok(approvalRecordOf(detailOf(h.f, h.b), h.f));
        }),

    fileApproved: (documentId: string) =>
        respond<FileApprovedResult>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = detailOf(h.f, h.b);
            if (d.state !== "approved") return refuse("not_approved", 409);
            const version = d.version ?? "1.0";
            const filed = (store.reports[h.b.id] ?? []).find((r) => r.documentId === d.id && r.nextVersion === version);
            if (filed && (!d.lastPost || d.lastPost.at <= filed.filedAt)) return refuse("already_filed", 409);
            const next = nextVersion(version);
            const docType = d.docType ?? "Document";
            const title = docsStoreApi.title(h.f).split("\n")[0];
            // The record keeps the version each question was approved in.
            for (const t of h.f.threads ?? []) {
                for (const r of t.replies) {
                    if (r.action === "resolve" && !r.docVersion) r.docVersion = version;
                }
            }
            const report: ApprovedReport = {
                id: nextKey("rep"),
                kind: "doc_approved",
                documentId: d.id,
                title: `${title}, approved v${version}`,
                fileName: `${docNameOf({ client: h.b.name, docType, codes: d.codes, location: d.outlet, version, date: today() })}.pdf`,
                url: `https://drive.google.com/file/d/1Approved${d.id.replace(/[^A-Za-z0-9]/g, "")}${version.replace(".", "")}/view`,
                version,
                nextVersion: next,
                filedAt: now(),
                filedBy: staff(),
                codes: d.codes,
                outlet: d.outlet,
            };
            store.reports[h.b.id] = [report, ...(store.reports[h.b.id] ?? [])];
            // The next version starts on the same click: the name and the version line.
            h.f.name = docNameOf({ client: h.b.name, docType, codes: d.codes, location: d.outlet, version: next, date: today() });
            h.f.nameDate = null;
            const [line1 = title, , ...rest] = (h.f.pageTitle ?? title).split("\n");
            h.f.pageTitle = [line1, versionLineOf(h.b.name, next), ...rest].join("\n");
            touch(h.f);
            // Bell only (YW's default, 2 Oct 2026).
            docsStoreApi.notice({ audience: "client", businessId: h.b.id, title: `In your reports: ${merchantDocName(title)}, approved v${version}`, body: "", link: "/oura/reports" });
            return ok({ document: detailOf(h.f, h.b), report });
        }),

    listReports: (businessId: string) => read<ApprovedReport[]>(() => ok(store.reports[businessId] ?? [])),

    getSummary: (documentId: string) =>
        read<DocSummary | null>(() => {
            if (!hit(documentId)) return notFound();
            return ok(store.summaries[documentId] ?? null);
        }),

    makeSummary: (documentId: string) =>
        respond<DocSummary>(() => {
            const h = hit(documentId);
            if (!h) return notFound();
            const d = detailOf(h.f, h.b);
            const have = store.summaries[documentId];
            // Made only when the doc changed since the last one: no spend in the background.
            if (have && have.madeFrom === d.modifiedAt) return ok(have);
            const made = summaryOf(d);
            store.summaries[documentId] = made;
            return ok(made);
        }),

    uploadClientFile: (name: string) =>
        respond<ClientFile>(() => {
            if (!name.trim()) return refuse("answer_invalid", 400, "Pick a file.");
            const key = nextKey("file");
            const file: ClientFile = {
                fileKey: key,
                name: name.trim(),
                url: `https://drive.google.com/file/d/1ClientFile${key.replace(/[^A-Za-z0-9]/g, "")}/view`,
                folderPath: "Nyonya Table / Files from Siew Ling",
            };
            store.clientFiles[key] = file;
            return ok(file);
        }),

    answer: (documentId: string, threadId: string, body: ClientAnswerIn) =>
        respond<MerchantDocument>(() => {
            const h = hit(documentId);
            const t = h && threadOf(h.f, threadId);
            if (!h || !t) return notFound("That question is no longer in the document.");
            const computed = detailOf(h.f, h.b).threads.find((x) => x.id === threadId);
            if (!computed || computed.kind !== "question" || !detailOf(h.f, h.b).sharing.clientCanSee) return notFound("That question is no longer in the document.");
            if (computed.resolved) return refuse("thread_resolved", 409);
            const client = docsStoreApi.clientOf(h.f, h.b);
            const kind = body.kind;
            let text: string;
            let file: ClientFile | null = null;
            if (kind === "agree") text = "Agreed.";
            else if (kind === "change") {
                if (!body.note?.trim()) return refuse("note_required", 400);
                text = `Change: ${body.note.trim()}`;
            } else if (kind === "option") {
                const options = computed.options ?? [];
                if (!body.option || !options.includes(body.option)) return refuse("option_unknown", 400);
                text = `Chose: ${body.option}`;
            } else if (kind === "text") {
                if (!body.text?.trim()) return refuse("answer_invalid", 400, "Write your answer first.");
                text = body.text.trim();
                // Lane 5: a file may go with the words (a price list typed out, and its photo).
                file = body.fileKey ? store.clientFiles[body.fileKey] ?? null : null;
            } else if (kind === "file") {
                const all = (body.fileKeys?.length ? body.fileKeys : body.fileKey ? [body.fileKey] : []).map((k) => store.clientFiles[k]).filter((x): x is ClientFile => Boolean(x));
                file = all[0] ?? null;
                if (!file) return refuse("answer_invalid", 400, "Add the file first.");
                text = all.length > 1 ? `Sent ${all.length} files: ${all.map((x) => x.name).join(", ")}` : `Sent a file: ${file.name}`;
            } else {
                return refuse("answer_invalid", 400);
            }
            t.replies.push(onBehalfReply(client, text, { answer: kind, file: file ? { name: file.name, url: file.url } : null }));
            // Agree resolves on the client's behalf: the approval of record. Change leaves it with us.
            if (kind === "agree") t.replies.push(onBehalfReply(client, "", { action: "resolve", answer: "agree" }));
            touch(h.f);
            const title = docsStoreApi.title(h.f).split("\n")[0];
            docsStoreApi.notice({ audience: "staff", businessId: h.b.id, title: `${client.name} answered in ${title}`, body: trim(text, 200), link: `/oura/businesses/${h.b.id}/reviews?doc=${h.f.id}` });
            const mine = docsStoreApi.merchantDocs(h.b.id)?.find((m) => m.id === h.f.id);
            return mine ? ok(mine) : notFound("That document is no longer shared with you.");
        }),
};

function writeStatus(): DocsWriteStatus {
    const checked = new Date(Math.floor(Date.now() / 300_000) * 300_000).toISOString();
    const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(checked)) / 60_000));
    return {
        actingAs: OURA_ACCOUNT,
        drive: "write",
        docs: "write",
        lastWriteCheckAt: checked,
        detail: `Writes client docs as ${OURA_ACCOUNT}. Checked ${minutes === 0 ? "just now" : `${minutes} min ago`}.`,
    };
}

function shareCheckOf(f: MockFile, b: MockBusiness): ShareCheck {
    const d = detailOf(f, b);
    const items: ShareCheckItem[] = [];
    if (d.sharing.linkSharing) {
        const can = d.sharing.linkSharing === "writer" ? "edit it" : d.sharing.linkSharing === "commenter" ? "comment on it" : "open it";
        items.push({ code: "link_sharing", level: "fix", message: `Turn link sharing off. Anyone with the link can ${can}.`, detail: null });
    }
    const editors = d.sharing.sharedWith.filter((p) => !p.ours && p.role === "writer");
    if (editors.length) items.push({ code: "edit_access", level: "fix", message: "Make them a commenter. The rule is comment-only.", detail: editors.map((p) => p.email ?? p.name ?? "Someone") });
    // Our notes are said, not blocked: a person deletes them in the doc (lane 3).
    const internal = d.threads.filter((t) => t.internal && !t.resolved);
    if (internal.length) items.push({ code: "internal_notes", level: "warn", message: `${plural(internal.length, "internal note")} in the doc. The client would read ${internal.length === 1 ? "it" : "them"}. Delete ${internal.length === 1 ? "it" : "them"} in the doc first.`, detail: internal.map((t) => trim(t.text, 120)) });
    const fromPerms = d.sharing.sharedWith.filter((p) => !p.ours && p.email && p.role !== "owner").map((p) => p.email as string);
    const client = docsStoreApi.clientOf(f, b);
    return { ok: !items.some((i) => (i.level ?? "fix") === "fix"), items, suggestedEmails: fromPerms.length ? fromPerms : [client.email] };
}

/**
 * Route 12's lane 5 field on one shared doc: the questions the client
 * answered that now wait on us or are closed (their last words, when, and
 * where).
 */
function merchantExtras(m: MerchantDocument): MerchantDocument {
    const h = hit(m.id);
    if (!h) return { ...m, answered: [] };
    const d = detailOf(h.f, h.b);
    const answered: MerchantAnswered[] = [];
    for (const t of d.threads) {
        if (t.kind !== "question" || t.internal) continue;
        // Back with them (we replied after their answer): it is in openQuestions instead.
        if (!t.resolved && t.state !== "needs_us") continue;
        const theirs = [...t.posts].reverse().find((p) => p.author.side === "client" && p.action === "reply" && (p.text.trim() || p.file));
        const resolvedByThem = t.resolved && t.resolvedBy?.side === "client";
        if (!theirs && !resolvedByThem) continue;
        const rec = threadOf(h.f, t.id)?.question ?? null;
        answered.push({
            id: t.id,
            questionId: t.questionId ?? null,
            number: t.number ?? null,
            text: rec ? rec.text : t.text.replace(/^\s*\[[^\]]*\]\s*/, ""),
            answer: theirs?.answer ?? null,
            words: theirs?.text ?? "",
            file: theirs?.file ?? null,
            at: theirs ? theirs.saidAt ?? theirs.at : t.resolvedAt ?? t.openedAt,
            via: theirs ? theirs.via ?? (theirs.where === "doc" ? "doc" : "oura") : "doc",
            state: t.resolved ? "approved" : "with_us",
        });
    }
    answered.sort((x, y) => y.at.localeCompare(x.at));
    return { ...m, answered };
}

/* ------------------------------------------------------------------------- *
 * Wiring: the Drive fixtures, route 8's questionList, route 1's write line
 * ------------------------------------------------------------------------- */

if (DOCS_WRITE_SAMPLE_SERVES) {
    registerDocsSeedExtension("docs-write", SEED_REV, seedDriveFixtures);
    registerDocsDecorators({
        detail: (d: DocumentDetail) => {
            const h = hit(d.id);
            return { ...d, questionList: questionListOf(d, h?.f ?? null), questionCheck: questionCheckOf(h?.f ?? null) };
        },
        connection: (c: DocsConnection) => ({ ...c, write: writeStatus() }),
        merchant: (docs: MerchantDocument[]) => docs.map(merchantExtras),
        // B24: a filed PDF is a report row of kind doc_approved on the client page, named as their list names the doc.
        reports: (businessId: string) =>
            (store.reports[businessId] ?? []).map((r) => {
                const f = hit(r.documentId)?.f;
                return { id: r.id, title: `${merchantDocName(f ? docsStoreApi.title(f) : r.title.replace(/, approved v[\d.]+$/, ""))}, approved v${r.version}`, date: myDate(r.filedAt), url: r.url, kind: "doc_approved" as const, pages: null, summary: `The version you approved, filed by ${r.filedBy}. Anything changed after this comes to you as v${r.nextVersion}.` };
            }),
    });
    if (typeof window !== "undefined") {
        (window as unknown as Record<string, unknown>).__ouraResetDocsWrite = resetDocsWriteStore;
    }
}

/** The seeded docs, for screens' dev notes and tests: approved (F3), not shared with a draft question (F7), answered in Oura (F2). */
export { F3 as SAMPLE_APPROVED_DOC_ID, F7 as SAMPLE_UNSHARED_DOC_ID, F2 as SAMPLE_ANSWERED_DOC_ID };
